import React, { useEffect, useState } from 'react'
import {
	Grid,
	List,
	ListItem,
	ListItemText,
	ListItemIcon,
	Box,
	Paper,
	Typography,
	Button,
	CircularProgress,
	useTheme,
	alpha,
	Avatar,
	Checkbox,
	FormControl,
	Autocomplete,
	TextField,
} from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import ContactlessIcon from '@mui/icons-material/Contactless'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import { DirectReportAttendance } from '../../../types/Employee/Attendance'
import {
	getDirectReportsAttendance,
	signInWorkersBulk,
} from '../../../services/attendanceService'
import { getProjectAreas } from '../../../services/projectService'
import { getProjectMapImage } from '../../../services/attachmentService'
import { WorkArea } from '../../../types/Project/WorkArea'
import { WorkAreaSelectionModal } from './WorkAreaSelectionModal'
import { useNFCReader } from '../../hooks/useNFCReader'
import { useProjectFeaturesContext } from '../../project/contexts/ProjectFeaturesContext'
import { cardSignIn } from '../../../services/attendanceService'
import ConsentDialog from './ConsentDialog'
import { useConsentContext } from './ConsentContext'
import { AttendanceLayoutVariant, attendanceCardBodySx, attendanceCardGridSx, attendanceCardPanelHeightSx } from './attendanceLayout'
import { AttendanceBody, AttendanceShell } from './AttendanceShell'

type CardSignOnDialogProps = {
	supervisorId?: number
	department: string
	open: boolean
	variant?: AttendanceLayoutVariant
}

export const CardSignOnDialog = ({
	supervisorId,
	department,
	open,
	variant = 'dialog',
}: CardSignOnDialogProps): JSX.Element => {
	const theme = useTheme()
	const [attendances, setAttendances] = useState<DirectReportAttendance[]>([])
	const [loadingAttendances, setLoadingAttendances] = useState(false)
	const [workAreas, setWorkAreas] = useState<WorkArea[]>([])
	const [mapUrl, setMapUrl] = useState<string>('')
	const [scannedWorker, setScannedWorker] = useState<DirectReportAttendance | null>(null)
	const [selectedArea, setSelectedArea] = useState<WorkArea | null>(null)
	const [loadingSignIn, setLoadingSignIn] = useState(false)
	const [areaError, setAreaError] = useState(false)
	const [loadingNoShow, setLoadingNoShow] = useState<{ workerId: number; status: string } | null>(
		null,
	)
	const { nfcMessage, error, startReading, stopReading, setNfcMessage } = useNFCReader()
	const { loadConsentTexts, getSignature, clearSignatures, generateConsentForms } =
		useConsentContext()
	const { isFeatureEnabled } = useProjectFeaturesContext()
	const isDaysOffEnabled = isFeatureEnabled(3)
	const isWorkAreaEnabled = isFeatureEnabled(5)
	const isConsentEnabled = isFeatureEnabled(6)
	const loadAttendances = async () => {
		const data = await getDirectReportsAttendance(department, supervisorId, false, null)
		setAttendances(data)
	}
	useEffect(() => {
		if (open) {
			;(async () => {
				setLoadingAttendances(true)

				try {
					await loadAttendances()
				} catch (error) {
					console.error('Error loading attendance', error)
				} finally {
					setLoadingAttendances(false)
				}

				const [areas, mapUrl, _] = await Promise.all([
					getProjectAreas(),
					isWorkAreaEnabled ? getProjectMapImage() : Promise.resolve(''),
					isConsentEnabled ? loadConsentTexts() : Promise.resolve(),
				])
				setWorkAreas(areas)
				setMapUrl(mapUrl || '')
				startReading()
			})()
		} else {
			setScannedWorker(null)
			setSelectedArea(null)
			setAreaError(false)
			stopReading()
		}
		// cleanup on unmount or dependencies change
		return () => {
			stopReading()
			clearSignatures()
		}
	}, [open, department, supervisorId, startReading, stopReading])

	useEffect(() => {
		if (nfcMessage) {
			const worker = attendances.find((a) => a.cardId === nfcMessage.cardId)
			if (!worker) {
				alert('Worker not found')
				return
			}
			setScannedWorker(worker)
			setSelectedArea(null)
			setAreaError(false)
		}
	}, [nfcMessage, attendances])

	const handleAreaSelected = (area: WorkArea) => {
		setSelectedArea(area)
		setAreaError(false)
	}

	const handleConfirm = async () => {
		if (!scannedWorker || !nfcMessage) {
			return
		}

		if (!selectedArea && isWorkAreaEnabled) {
			setAreaError(true)
			return
		}

		if (isConsentEnabled && !getSignature(scannedWorker.workerId)) {
			setAttendances((prev) =>
				prev.map((worker) =>
					worker.workerId === scannedWorker.workerId ? { ...worker, consentError: true } : worker,
				),
			)
			return
		}

		const forms = isConsentEnabled ? generateConsentForms([scannedWorker.workerId]) : []

		setLoadingSignIn(true)
		try {
			await cardSignIn(
				{
					cardId: nfcMessage.cardId,
					cardToken: nfcMessage.token,
					areaId: selectedArea?.id,
				},
				forms,
			)
		} finally {
			await loadAttendances()
			setNfcMessage(null)
			setLoadingSignIn(false)
			setScannedWorker(null)
			setSelectedArea(null)
			setAreaError(false)
		}
	}

	async function handleNoShow(newStatus: string, workerId: number): Promise<void> {
		const updatedAttendance = attendances.find((att) => att.workerId === workerId)
		if (!updatedAttendance) return

		const changedAttendance = [
			{
				...updatedAttendance,
				status: newStatus,
			},
		]

		try {
			setLoadingNoShow({ workerId, status: newStatus })
			await signInWorkersBulk(changedAttendance)
			await loadAttendances()
		} finally {
			setLoadingNoShow(null)
		}
	}
	function handleConsentChange(): void {
		setScannedWorker((prev) => (prev ? { ...prev, consentError: false } : null))
	}
	return (
		<AttendanceShell variant={variant}>
			<AttendanceBody variant={variant} sx={attendanceCardBodySx(theme, variant)}>
				<Grid container spacing={3} sx={attendanceCardGridSx(variant)}>
				<Grid item xs={5} sx={variant === 'page' ? { display: 'flex', minHeight: 0 } : undefined}>
					<Paper
						variant='outlined'
						sx={{
							...attendanceCardPanelHeightSx(variant),
							overflowY: 'auto',
							background: alpha(theme.palette.grey[50], 0.8),
							borderRadius: 2,
							boxShadow: theme.shadows[1],
							border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
						}}
					>
						{loadingAttendances ? (
							<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
								<CircularProgress size={40} />
							</Box>
						) : (
							<List>
								{attendances.map((att) => (
									<ListItem
										key={att.workerId}
										selected={scannedWorker?.workerId === att.workerId}
										sx={{
											width: '100%',
											px: 2,
											py: 1.5,
											borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
											'&:last-child': {
												borderBottom: 'none',
											},
											'&:hover': {
												backgroundColor: alpha(theme.palette.primary.main, 0.04),
											},
											'&.Mui-selected': {
												backgroundColor: alpha(theme.palette.primary.main, 0.08),
												'&:hover': {
													backgroundColor: alpha(theme.palette.primary.main, 0.12),
												},
											},
											gap: 2,
										}}
									>
										<ListItemIcon>
											<Avatar
												sx={{
													bgcolor: theme.palette.primary.main,
													width: 40,
													height: 40,
												}}
											>
												<PersonIcon />
											</Avatar>
										</ListItemIcon>
										<ListItemText
											primary={
												<Typography variant='subtitle1' fontWeight='medium'>
													{att.fullName}
												</Typography>
											}
											secondary={
												att.status === 'Present' && !att.signOutAt ? (
													<Typography variant='body2' color='text.secondary'>
														Signed In At {workAreas.find((wa) => wa.id === att.areaId)?.name}
													</Typography>
												) : undefined
											}
										/>
										<Box
											sx={{
												display: 'flex',
												gap: 1,
												minHeight: '40px',
												maxHeight: '40px',
												alignItems: 'center',
												flexShrink: 0,
											}}
										>
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'center',
													alignItems: 'center',
													padding: '4px',
													border:
														att.status === 'Absent' ? '2px solid #1976d2' : '2px solid transparent',
													borderRadius: '8px',
													height: '100%',
													flexShrink: 0,
												}}
											>
												<Button
													color='primary'
													variant='contained'
													onClick={(e) => {
														e.stopPropagation()
														e.preventDefault()
														handleNoShow('Absent', att.workerId)
													}}
													size='small'
													sx={{ height: '100%', flexShrink: 0 }}
													startIcon={
														loadingNoShow &&
														loadingNoShow.workerId === att.workerId &&
														loadingNoShow.status === 'Absent' ? (
															<CircularProgress color='inherit' size={24} sx={{ p: 0, m: 0 }} />
														) : (
															<Checkbox
																checked={att.status === 'Absent'}
																sx={{
																	color: 'white',
																	'&.Mui-checked': {
																		color: 'white',
																	},
																	padding: 0,
																	margin: 0,
																}}
															/>
														)
													}
												>
													Absent
												</Button>
											</Box>
											{isDaysOffEnabled && (
												<Box
													sx={{
														display: 'flex',
														justifyContent: 'center',
														alignItems: 'center',
														padding: '4px',
														border:
															att.status === 'Days Off'
																? '2px solid #1976d2'
																: '2px solid transparent',
														borderRadius: '8px',
														height: '100%',
														flexShrink: 0,
													}}
												>
													<Button
														color='warning'
														variant='contained'
														size='small'
														onClick={(e) => {
															e.stopPropagation()
															e.preventDefault()
															handleNoShow('Days Off', att.workerId)
														}}
														sx={{ height: '100%', flexShrink: 0 }}
														startIcon={
															loadingNoShow &&
															loadingNoShow.workerId === att.workerId &&
															loadingNoShow.status === 'Days Off' ? (
																<CircularProgress color='inherit' size={24} sx={{ p: 0, m: 0 }} />
															) : (
																<Checkbox
																	checked={att.status === 'Days Off'}
																	sx={{
																		color: 'white',
																		'&.Mui-checked': {
																			color: 'white',
																		},
																		padding: 0,
																		margin: 0,
																	}}
																/>
															)
														}
													>
														Days Off
													</Button>
												</Box>
											)}
										</Box>
									</ListItem>
								))}
							</List>
						)}
					</Paper>
				</Grid>
				<Grid item xs={7} sx={variant === 'page' ? { display: 'flex', minHeight: 0 } : undefined}>
					<Paper
						variant='outlined'
						sx={{
							...attendanceCardPanelHeightSx(variant),
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							p: 3,
							background: alpha(theme.palette.grey[50], 0.8),
							borderRadius: 2,
							boxShadow: theme.shadows[1],
							border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
						}}
					>
						{!scannedWorker ? (
							<Box
								sx={{
									border: `2px dashed ${alpha(theme.palette.primary.main, 0.5)}`,
									borderRadius: '12px',
									width: '100%',
									height: '100%',
									display: 'flex',
									flexDirection: 'column',
									justifyContent: 'center',
									alignItems: 'center',
									gap: 2,
									background: alpha(theme.palette.primary.main, 0.02),
									'&:hover': {
										borderColor: theme.palette.primary.main,
										background: alpha(theme.palette.primary.main, 0.04),
									},
								}}
							>
								<ContactlessIcon
									sx={{
										fontSize: 60,
										color: 'primary.main',
										opacity: 0.7,
									}}
								/>
								<Typography
									variant='h5'
									color='primary'
									fontWeight='medium'
									sx={{
										textAlign: 'center',
										maxWidth: '80%',
									}}
								>
									Waiting for NFC card...
								</Typography>
								{error && (
									<Box
										sx={{
											mt: 1,
											p: 1.5,
											background: alpha(theme.palette.error.main, 0.1),
											borderRadius: 1,
											border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
										}}
									>
										<Typography
											color='error'
											variant='body2'
											sx={{
												fontWeight: 'medium',
											}}
										>
											{error}
										</Typography>
									</Box>
								)}
							</Box>
						) : (
							<Box
								sx={{
									width: '100%',
									textAlign: 'center',
									display: 'flex',
									flexDirection: 'column',
									gap: 2,
									alignItems: 'center',
								}}
							>
								<Avatar
									sx={{
										bgcolor: theme.palette.primary.main,
										width: 80,
										height: 80,
										mb: 2,
									}}
								>
									<PersonIcon sx={{ fontSize: 40 }} />
								</Avatar>
								<Typography
									variant='h5'
									fontWeight='medium'
									sx={{
										color: 'primary.main',
										textTransform: 'uppercase',
										letterSpacing: 1,
										mb: 1,
									}}
								>
									{scannedWorker.fullName}
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
									<LocationOnIcon color='action' />
									<Typography variant='body2' color='text.secondary'>
										Select Work Area
									</Typography>
								</Box>
								{isWorkAreaEnabled &&
									(mapUrl === '' ? (
										<FormControl sx={{ width: '50%' }}>
											<Autocomplete
												size={'small'}
												options={workAreas || []}
												getOptionLabel={(wa: WorkArea) => wa.name}
												value={workAreas?.find((wa) => wa.id === selectedArea?.id) || null}
												onChange={(_, newValue) => newValue && handleAreaSelected(newValue)}
												renderInput={(params) => (
													<TextField {...params} label='Choose Location' error={areaError} />
												)}
											/>
										</FormControl>
									) : (
										<WorkAreaSelectionModal
											workAreas={workAreas}
											imageUrl={mapUrl}
											setSelected={handleAreaSelected}
											selected={selectedArea?.name || null}
											error={areaError}
										/>
									))}
								{isWorkAreaEnabled && areaError && (
									<Box
										sx={{
											p: 1.5,
											background: alpha(theme.palette.error.main, 0.1),
											borderRadius: 1,
											border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
										}}
									>
										<Typography
											color='error'
											variant='body2'
											sx={{
												fontWeight: 'medium',
											}}
										>
											Please select a work area before confirming.
										</Typography>
									</Box>
								)}

								{isConsentEnabled && (
									<ConsentDialog
										type='dayStartPrompt'
										workerId={scannedWorker.workerId}
										error={scannedWorker.consentError}
										onConsentChange={handleConsentChange}
									/>
								)}

								<Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
									{!loadingSignIn ? (
										<Button
											variant='contained'
											color='primary'
											disabled={
												!selectedArea || (isConsentEnabled && !getSignature(scannedWorker.workerId))
											}
											onClick={handleConfirm}
											sx={{
												minWidth: 150,
												py: 1.5,
												fontWeight: 'medium',
												boxShadow: theme.shadows[1],
												'&:hover': {
													boxShadow: theme.shadows[2],
												},
											}}
										>
											Confirm Sign In
										</Button>
									) : (
										<CircularProgress size={32} sx={{ p: 0, m: 0 }} />
									)}
									<Button
										color='secondary'
										onClick={() => setScannedWorker(null)}
										sx={{
											color: 'primary.main',
											minWidth: 150,
											py: 1.5,
											fontWeight: 'medium',
											border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
											background: alpha(theme.palette.grey[100], 0.8),
											'&:hover': {
												background: alpha(theme.palette.grey[200], 0.8),
											},
										}}
									>
										Cancel
									</Button>
								</Box>
							</Box>
						)}
					</Paper>
				</Grid>
			</Grid>
			</AttendanceBody>
		</AttendanceShell>
	)
}
